/**
 * Presentation rules for step 5 (f10.8) -- badges, delta tone, the USD hint
 * and the transformation list. Nothing here computes a rate: every rate, delta
 * and capability is the server's decimal string, rendered verbatim.
 */

import type { ExchangeRatesReview } from '@/api/client';

export { isParsing, refusalMessage, severityPill } from '../usEngagements/usEngagementsView';

export const COMPARISON_CURRENCY = 'USD';

const STATUS_PILLS: Readonly<Record<string, string>> = {
  VALID: 'pill-success',
  NOT_IN_LOOKUP: 'pill-warning',
  INVALID: 'pill-danger',
};

export function statusPill(code: string): string {
  return `badge ${STATUS_PILLS[code] ?? 'pill-neutral'}`;
}

/** Up or down, read from the sign of the server's delta string -- never parsed to a number. */
export function deltaClass(delta: string | null): string {
  if (!delta || /^-?0(\.0*)?$/.test(delta)) return '';
  return delta.startsWith('-') ? 'fx-delta-down' : 'fx-delta-up';
}

/** "0.860067100000" -> "0.8600671": trailing zeros trimmed for the sentence only. */
export function trimRate(rate: string): string {
  return rate.includes('.') ? rate.replace(/0+$/, '').replace(/\.$/, '') : rate;
}

/** The line under the USD row (design D8). */
export function usdHint(usdRate: string, inverse: string): string {
  return (
    `CU US DB (EUR) figures are shown in USD by dividing by ${trimRate(usdRate)} ` +
    `(1 EUR = ${inverse} USD)`
  );
}

export interface Transformation {
  ok: boolean;
  text: string;
}

/** "What the system did with it", stated from the batch's own facts. */
export function transformations(review: ExchangeRatesReview): Transformation[] {
  const { batch, figures } = review;
  const trailer = review.issues.items.find((issue) => issue.code === 'trailer_dropped');
  const list: Transformation[] = [
    {
      ok: true,
      text:
        `Header found on row ${batch.detected_header_row ?? '—'}; Currency and Exchange Rate bound by name`,
    },
    { ok: true, text: trailer ? `Trailer dropped — ${trailer.message}` : 'No trailer after the data' },
    {
      ok: true,
      text: `${figures.rates_to_write} rates kept as exact decimals (EUR per 1 unit, 12 dp) — never floats`,
    },
  ];
  if (figures.currencies_not_in_lookup > 0) {
    list.push({
      ok: false,
      text: `${figures.currencies_not_in_lookup} currencies are not Atlas currencies — listed, not stored`,
    });
  }
  if (figures.rows_invalid > 0) {
    list.push({ ok: false, text: `${figures.rows_invalid} rows are invalid — discard and upload a corrected file` });
  }
  if (batch.rejection_reason) list.push({ ok: false, text: batch.rejection_reason });
  return list;
}
